import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {ClickEventHandler} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {FilterApplyFooter, FilterApplyFooterProps} from '../common/filterApplyFooter';
import {FilterRightClearButton} from '../common/filterClearRightButton';
import {BaseFilterDropdownProps} from '../common/types';
import {ModalFilterItem, ModalFilterItemProps} from './modalFilterItem';

/*
 * Props.
 */

export interface ModalFilterDropdownProps extends BaseFilterDropdownProps {
  title: ReactNode;
  filters: ReadonlyArray<ModalFilterItemProps>;
  footerHandlers: FilterApplyFooterProps;
  shouldCloseOnClear: boolean;

  // Indicates whether or not there is a current selection in the filter. When
  // true, the clear button is shown
  isActive: boolean;
  /** Called when the clear button is clicked */
  onClear: ClickEventHandler;
  /** Optionally override the clear button's default text */
  clearButtonLabel?: ReactNode;
}

/*
 * Constants.
 */

const defaultDropdownWidth = 300;

/*
 * Styles.
 */

const ModalFilterItemsWrapperDiv = styled.div`
  padding: 12px;
`;

/*
 * Component.
 */

export const ModalFilterDropdown: FC<ModalFilterDropdownProps> = (props) => {
  const {title, footerHandlers, isActive, onClear, shouldCloseOnClear, clearButtonLabel} = props;

  const onClearClicked: ClickEventHandler = (event) => {
    if (props.onRequestClose && shouldCloseOnClear) {
      props.onRequestClose();
    }
    onClear(event);
  };

  const clearButton = (
    <FilterRightClearButton onClear={onClearClicked}>{clearButtonLabel}</FilterRightClearButton>
  );
  const rightIcon = isActive ? clearButton : undefined;

  return (
    <Dropdown {...props} width={defaultDropdownWidth}>
      <DropdownHeader title={title} rightIcon={rightIcon} />
      {renderContent(props)}
      {footerHandlers && (
        <FilterApplyFooter
          onApplyClicked={footerHandlers.onApplyClicked}
          onCancelClicked={footerHandlers.onCancelClicked}
          onRequestClose={props.onRequestClose}
        />
      )}
    </Dropdown>
  );
};

/*
 * Helpers.
 */

function renderContent(props: ModalFilterDropdownProps) {
  const {filters} = props;

  return (
    <ModalFilterItemsWrapperDiv>
      {filters.map((filter) => {
        const {id, value, isChecked, onSelect, renderDropdown} = filter;
        return (
          <ModalFilterItem
            key={`modal-filter-${id}`}
            id={id}
            value={value}
            isChecked={isChecked}
            onSelect={onSelect}
            renderDropdown={renderDropdown}
          />
        );
      })}
    </ModalFilterItemsWrapperDiv>
  );
}
